<template>
  <div class="create-project">
    <h1>Create a Project<HelpLink topic="projects" section="creating-a-project" title="How to create a project" /></h1>
    <form @submit.prevent="submitForm">
      <div class="form-group">
        <label for="creation_mode">Creation Mode</label>
        <select id="creation_mode" v-model="project.creation_mode" class="form-control">
          <option value="scratch">Create from scratch</option>
          <option value="template">Create from template</option>
        </select>
      </div>

      <div v-if="project.creation_mode === 'scratch'" class="form-group">
        <label for="structure_set_id">Structure Set</label>
        <HelpLink topic="project-structure" section="the-built-in-structure-sets" title="Which structure set should I choose?" />
        <select
          id="structure_set_id"
          v-model="project.structure_set_id"
          class="form-control"
          :disabled="loadingStructureSets"
        >
          <option :value="null">Select a structure set</option>
          <option v-for="set in structureSets" :key="set.id" :value="set.id">
            {{ set.name }}
          </option>
        </select>
        <div v-if="loadingStructureSets" class="helper-text">Loading structure sets...</div>
        <div v-else-if="!structureSets.length" class="helper-text">No active structure sets found.</div>
        <div v-if="validationErrors.includes('structure_set_id')" class="error-feedback">Structure set is required.</div>
      </div>

      <div v-if="project.creation_mode === 'template'" class="form-group">
        <label for="template_id">Project Template</label>
        <HelpLink topic="project-templates" title="About project templates" />
        <select
          id="template_id"
          v-model="project.template_id"
          class="form-control"
          :disabled="loadingTemplates"
        >
          <option :value="null">Select a template</option>
          <option v-for="template in templates" :key="template.id" :value="template.id">
            {{ template.name }}
          </option>
        </select>
        <div v-if="loadingTemplates" class="helper-text">Loading templates...</div>
        <div v-else-if="!templates.length" class="helper-text">No saved templates found for your account.</div>
        <div v-if="validationErrors.includes('template_id')" class="error-feedback">Template selection is required when creating from template.</div>
      </div>

      <div class="form-group">
        <label for="name">Name</label>
        <input type="text" id="name" v-model="project.name" @input="validateName" :class="{'is-invalid': validationErrors.includes('name')}" required class="form-control">
        <div v-if="validationErrors.includes('name')" class="error-feedback">Name is required.</div>
      </div>
      <div class="form-group">
        <label for="project_number">Project Number</label>
        <input type="text" id="project_number" v-model="project.project_number" @input="validateProjectNumber" :class="{'is-invalid': validationErrors.includes('project_number')}" required class="form-control">
        <div v-if="validationErrors.includes('project_number')" class="error-feedback">Project Number is required.</div>
      </div>
      <div class="form-group">
        <label for="status">Status</label>
        <select id="status" v-model="project.status" class="form-control">
          <option value="Sales Opportunity">Sales Opportunity</option>
          <option value="To be Started">To be Started</option>
          <option value="Ongoing">Ongoing</option>
          <option value="Finished">Finished</option>
        </select>
      </div>
      <div class="form-group">
        <label for="start_date">Start Date</label>
        <input type="date" id="start_date" v-model="project.start_date" class="form-control">
      </div>
      <div class="form-group">
        <label for="end_date">End Date</label>
        <input type="date" id="end_date" v-model="project.end_date" class="form-control">
      </div>
      <button type="submit" class="submit-button" :disabled="loading">
        <span v-if="loading" class="spinner"></span>
        <span v-else>Create Project</span>
      </button>
    </form>
    <div v-if="loading" class="overlay">
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { API_ENDPOINTS } from '@/services/config';
import apiService from '@/services/apiService';
import { mapState } from 'vuex';

const FORM_CACHE_KEY = "project_form";
const CACHE_EXPIRATION = 10 * 60 * 1000; // 10 minutes in milliseconds

export default {
  data() {
    const today = new Date();
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(today.getFullYear() + 1);

    return {
      project: {
        creation_mode: 'scratch',
        template_id: null,
        structure_set_id: null,
        name: '',
        project_number: '',
        status: 'Sales Opportunity',
        start_date: today.toISOString().split('T')[0],
        end_date: oneYearFromNow.toISOString().split('T')[0],
      },
      validationErrors: [],
      loading: false,
      loadingTemplates: false,
      loadingStructureSets: false,
      templates: [],
      structureSets: [],
    };
  },
  computed: {
    ...mapState(['loggedInUser']), 
    selectedTemplate() {
      const selectedTemplateId = Number(this.project.template_id);
      if (!selectedTemplateId) {
        return null;
      }

      return this.templates.find((template) => Number(template.id) === selectedTemplateId) || null;
    },
    selectedTemplateStructureSetId() {
      const raw = this.selectedTemplate?.project_structure_set_id
        ?? this.selectedTemplate?.structure_set_id
        ?? this.selectedTemplate?.source_project_structure_set_id
        ?? null;

      if (raw == null || raw === '') {
        return null;
      }

      const numeric = Number(raw);
      return Number.isNaN(numeric) ? null : numeric;
    },
    effectiveStructureSetId() {
      if (this.project.creation_mode === 'scratch') {
        return this.project.structure_set_id;
      }

      return this.selectedTemplateStructureSetId;
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  created() {
    this.loadCachedFormData();
    this.fetchProjectTemplates();
    this.fetchStructureSets();
  },
  methods: {
    async fetchProjectTemplates() {
      this.loadingTemplates = true;
      try {
        const response = await apiService.getProjectTemplates(
          this.loggedInUser.entra_id
        );
        this.templates = Array.isArray(response)
          ? response
          : response?.items || response?.templates || response?.data?.items || response?.data || [];
      } catch (error) {
        this.templates = [];
        console.error('Failed to fetch project templates:', error);
      } finally {
        this.loadingTemplates = false;
      }
    },

    async fetchStructureSets() {
      this.loadingStructureSets = true;
      try {
        const response = await apiService.getProjectStructureSets(this.loggedInUser.entra_id);
        const items = Array.isArray(response)
          ? response
          : response?.items || response?.sets || response?.data?.items || response?.data || [];
        this.structureSets = items.filter((item) => item?.active !== false);
      } catch (error) {
        this.structureSets = [];
        console.error('Failed to fetch structure sets:', error);
      } finally {
        this.loadingStructureSets = false;
      }
    },

    // Load cached form data if available and not expired
    loadCachedFormData() {
      const cachedData = JSON.parse(localStorage.getItem(FORM_CACHE_KEY));
      if (cachedData) {
        const { data, timestamp } = cachedData;
        const now = Date.now();

        if (now - timestamp < CACHE_EXPIRATION) {
          this.project = data;
        } else {
          localStorage.removeItem(FORM_CACHE_KEY); // Clear expired data
        }
      }
    },

    // Save form data to cache
    cacheFormData() {
      const cache = {
        data: this.project,
        timestamp: Date.now(),
      };
      localStorage.setItem(FORM_CACHE_KEY, JSON.stringify(cache));
    },

    validateName() {
      this.validateField('name');
    },
    validateProjectNumber() {
      this.validateField('project_number');
    },
    validateField(field) {
      if (field === 'template_id') {
        if (this.project.creation_mode === 'template' && !this.project.template_id) {
          this.addValidationError(field);
        } else {
          this.removeValidationError(field);
        }
        return;
      }

      if (field === 'structure_set_id') {
        if (this.project.creation_mode === 'scratch' && !this.project.structure_set_id) {
          this.addValidationError(field);
        } else {
          this.removeValidationError(field);
        }
        return;
      }

      if (!this.project[field]) {
        this.addValidationError(field);
      } else {
        this.removeValidationError(field);
      }
    },
    addValidationError(field) {
      if (!this.validationErrors.includes(field)) {
        this.validationErrors.push(field);
      }
    },
    removeValidationError(field) {
      this.validationErrors = this.validationErrors.filter(error => error !== field);
    },
    cancelSelection() {
      this.selectedFiles = [];
      this.$refs.fileInput.value = '';
    },
    validateForm() {
      this.validationErrors = [];

      // Ensure all required fields are not empty
      const requiredFields = ['name', 'project_number'];
      if (this.project.creation_mode === 'scratch') {
        requiredFields.push('structure_set_id');
      }
      requiredFields.forEach(field => {
        if (!this.project[field]) {
          this.addValidationError(field);
        }
      });

      if (this.project.creation_mode === 'template' && !this.project.template_id) {
        this.addValidationError('template_id');
      }

      if (this.project.creation_mode === 'template' && !this.effectiveStructureSetId) {
        this.addValidationError('structure_set_id');
      }

      return this.validationErrors.length === 0;
    },
    async submitProjectDetails() {
      if (!this.validateForm()) {
        this.toast.error("Form is invalid!");
        console.log("Form is invalid:", this.validationErrors);
        return;
      }

      const projectDetails = {
        name: this.project.name,
        project_number: this.project.project_number,
        status: this.project.status,
        start_date: this.project.start_date,
        end_date: this.project.end_date,
        structure_set_id: this.effectiveStructureSetId,
        template_id: this.project.creation_mode === 'template' ? this.project.template_id : null,
      };

      try {
        const response = await apiService.createResource(API_ENDPOINTS.projects, projectDetails, this.loggedInUser.entra_id);
        this.toast.success("Project created successfully!");

        // Check if the file input is defined and files are selected
        if (this.$refs.fileInput && this.$refs.fileInput.files.length > 0) {
          await apiService.uploadFiles("project", response.project_id, this.selectedFiles);
        }

        localStorage.removeItem(FORM_CACHE_KEY); // Clear cache after submission
        this.$router.push({ name: 'ProjectDetailPage', params: { id: response.project_id } });
      } catch (error) {
        if(error.response.status === 409){
          console.log(error.response)
          this.toast.error(error.response.data.message)
        }
        else{
          this.toast.error("Failed to create project!");
          console.error("Failed to submit project details:", error);
        }
      }
    },
    async submitForm() {
      this.loading = true;
      await this.submitProjectDetails();
      this.loading = false;
    },
  },
  watch: {
    // Automatically cache form data when it changes
    project: {
      handler() {
        this.cacheFormData();
      },
      deep: true,
    },
    'project.creation_mode'() {
      if (this.project.creation_mode !== 'template') {
        this.project.template_id = null;
        this.removeValidationError('template_id');
        this.validateField('structure_set_id');
      } else {
        this.project.structure_set_id = null;
        this.removeValidationError('structure_set_id');
        this.validateField('template_id');
      }
    },
    'project.template_id'() {
      if (this.project.creation_mode === 'template') {
        this.project.structure_set_id = this.selectedTemplateStructureSetId;
      }
      this.validateField('template_id');
    },
    'project.structure_set_id'() {
      if (this.project.creation_mode === 'scratch') {
        this.validateField('structure_set_id');
      }
    },
  },
};
</script>


<style scoped lang="scss">
@import "@/assets/main.scss";

.create-project {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);

  h1 {
    margin-bottom: 2rem;
    text-align: center;
  }

  .helper-text {
    margin-top: 0.35rem;
    font-size: 0.9rem;
    color: rgba(0, 0, 0, 0.65);
  }
}
</style>
